import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supportedLngs } from "../localization/options";

const languageLabels: Record<string, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  de: "Deutsch",
  pt: "Português",
  it: "Italiano",
};

const languageFlags: Record<string, string> = {
  en: "🇬🇧",
  fr: "🇫🇷",
  es: "🇪🇸",
  de: "🇩🇪",
  pt: "🇵🇹",
  it: "🇮🇹",
};

interface LanguageTogglerProps {
  textColor?: string;
}

export default function LanguageToggler({
  textColor = "#fff",
}: LanguageTogglerProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = i18n.language?.split("-")[0] || "en";

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors hover:bg-black/10"
        style={{ color: textColor }}
      >
        <span>{languageFlags[currentLang]}</span>
        <span className="hidden sm:inline">{languageLabels[currentLang]}</span>
        <svg
          className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full z-20 mt-1 min-w-35 rounded-lg bg-white py-1 shadow-lg">
            {supportedLngs.map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100 ${
                  currentLang === lang ? "bg-gray-50 font-medium" : ""
                }`}
              >
                <span>{languageFlags[lang]}</span>
                <span>{languageLabels[lang]}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
